import { useMemo, useState } from 'react';
import { FilterableMultiSelect, InlineNotification } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import {
  cohortIndexByWell,
  interestPct,
  splitWellsByInterest,
  wellRateKey,
} from './interest-modeling-carry';
import type { CarryWell } from './types';
import styles from './property-picker.module.scss';

/**
 * A property option in a group's well multi-select — a well, plus the optional
 * `isSelectAll` flag Carbon's `FilterableMultiSelect` reads to render its native
 * "Select all" row (a sentinel item at the top of `items`).
 */
export type PropertyOption = CarryWell & {
  isSelectAll?: boolean;
  /** Interest-cohort index — drives the menu's grouping order and its colour. */
  cohort?: number;
  /**
   * Deactivated because this group has already committed to another cohort. Carbon
   * reads `disabled` off the item (downshift's `isItemDisabled`), renders the row
   * inert, and still SHOWS it — which is the point: the property stays visible as
   * something that belongs to a different interest.
   */
  disabled?: boolean;
};

/** How many distinct cohort colours the menu cycles through. */
export const COHORT_COLOURS = 8;

/** The sentinel that turns Carbon's select-all row on (never a real selection). */
const SELECT_ALL_ITEM: PropertyOption = {
  id: '__select_all__',
  name: 'Select all',
  isSelectAll: true,
};

/**
 * The multi-select label for a property: the UWI prefixed before the well name
 * (`<uwi> - <name>`) when a UWI is available and distinct from the name, else the
 * name alone (so a well whose name already IS its UWI never reads `<uwi> - <uwi>`).
 *
 * @param {PropertyOption} [well] - The option (Carbon may pass null while filtering).
 * @return {string} The display label.
 */
export function propertyLabel(well?: PropertyOption | null): string {
  if (!well) {
    return '';
  }
  if (well.uwi && well.uwi !== well.name) {
    return `${well.uwi} - ${well.name}`;
  }
  return well.name;
}

/**
 * A property's interest for the mismatch message, e.g. `4.8243% NRI / 5.5135% WI`.
 *
 * @param {PropertyOption} [well] - The property.
 * @return {string} The rate pair, or a plain-English stand-in when unrated.
 */
function rateLabel(well?: PropertyOption | null): string {
  const nri = interestPct(well?.nri);
  const wi = interestPct(well?.wi);
  return nri === null || wi === null
    ? 'no certified interest'
    : `${nri}% NRI / ${wi}% WI`;
}

/** The shape the picker needs off a group — carry and election groups both satisfy it. */
export interface PickerGroup {
  id: string;
  name: string;
  wellIds: string[];
}

/** Props for {@link PropertyPicker}. */
export interface PropertyPickerProps {
  /** The group being edited. */
  group: PickerGroup;
  /** Every group on the tract — a property already in a sibling is off the menu. */
  groups: PickerGroup[];
  /** The tract's valued well roster. */
  wells: CarryWell[];
  /**
   * Applies an ACCEPTED selection. Handed the chosen wells (not just their ids) so
   * the caller can re-seed its own terms from the cohort the group now holds — what
   * that means differs by item kind (a carry group re-seeds its APO half, an
   * election group its participating interest), which is why it is the caller's job.
   */
  onChange: (wells: CarryWell[]) => void;
}

/**
 * The group's property multi-select: the roster ordered and colour-coded by interest
 * cohort, siblings' properties removed, other cohorts deactivated and sunk once the
 * group commits to one, and a rejection notice when a selection would put two
 * interests in one group (INS-10673).
 *
 * Shared by the OGL carry editor and the UMI election editor — the control is the
 * same in both, only the terms it re-seeds differ, and those belong to the caller.
 *
 * @param {PropertyPickerProps} props - The group, its siblings, the roster, and the apply callback.
 * @return {JSX.Element} The picker block.
 */
export function PropertyPicker({
  group,
  groups,
  wells,
  onChange,
}: PropertyPickerProps) {
  // Which interest cohort each property sits in. The menu is ordered and coloured by
  // this so the user can SEE where the tract's interest breaks, instead of being
  // handed a grouping they did not choose (INS-10673).
  const cohortOf = useMemo(() => cohortIndexByWell(wells), [wells]);
  // A property belongs to exactly one group, so anything a sibling already holds
  // leaves this menu entirely — not greyed, gone.
  const takenElsewhere = useMemo(
    () =>
      new Set(
        groups
          .filter((sibling) => sibling.id !== group.id)
          .flatMap((sibling) => sibling.wellIds)
      ),
    [groups, group.id]
  );
  // The cohort this group has committed to — read off the first property it holds.
  // Null while the group is empty, when every cohort is still open to it.
  const activeCohort =
    group.wellIds.length > 0 ? (cohortOf.get(group.wellIds[0]) ?? null) : null;
  // Roster position, to keep the order within a cohort stable across renders.
  const rosterOrder = useMemo(
    () => new Map(wells.map((well, index) => [well.id, index])),
    [wells]
  );
  // Prepend the select-all sentinel so Carbon renders its native "Select all" row.
  const propertyItems = useMemo<PropertyOption[]>(() => {
    const available = wells
      .filter((well) => !takenElsewhere.has(well.id))
      .map((well) => {
        const cohort = cohortOf.get(well.id) ?? 0;
        return {
          ...well,
          cohort,
          disabled: activeCohort !== null && cohort !== activeCohort,
        };
      });
    return [SELECT_ALL_ITEM, ...available];
  }, [wells, takenElsewhere, cohortOf, activeCohort]);

  // The selection MUST come from the SAME decorated objects the menu renders.
  // Carbon decides whether a row is checked with a deep-equality compare against
  // `selectedItems` (FilterableMultiSelect: `isEqual(selected, item)`), so a raw
  // roster well never matches a menu item carrying `cohort`/`disabled` and every
  // row draws unchecked — which is exactly what sandbox UAT saw.
  const selectedWells = useMemo<PropertyOption[]>(
    () =>
      propertyItems.filter(
        (item) => !item.isSelectAll && group.wellIds.includes(item.id)
      ),
    [propertyItems, group.wellIds]
  );

  /**
   * Menu order: the rows this group can still take first, grouped by interest, then
   * the deactivated cohorts parked at the bottom. Replaces Carbon's default sort
   * (which floats selected items to the top) — here the grouping IS the information,
   * so it has to survive selection.
   *
   * @param {PropertyOption[]} items - The menu items Carbon wants ordered.
   * @return {PropertyOption[]} The ordered items.
   */
  const sortByCohort = (items: PropertyOption[]): PropertyOption[] =>
    [...items].sort((a, b) => {
      if (!!a.disabled !== !!b.disabled) {
        return a.disabled ? 1 : -1;
      }
      const cohortDelta = (a.cohort ?? 0) - (b.cohort ?? 0);
      if (cohortDelta !== 0) {
        return cohortDelta;
      }
      return (rosterOrder.get(a.id) ?? 0) - (rosterOrder.get(b.id) ?? 0);
    });

  /**
   * One menu row: a colour swatch for its interest cohort, then the usual label. The
   * select-all sentinel belongs to no cohort and takes no swatch.
   *
   * @param {PropertyOption} item - The menu item (Carbon spreads it as props).
   * @return {JSX.Element} The row content.
   */
  const PropertyItem = (item: PropertyOption) => {
    if (item.isSelectAll) {
      return <span>{propertyLabel(item)}</span>;
    }
    const swatch = styles[`cohort${(item.cohort ?? 0) % COHORT_COLOURS}`];
    return (
      <span className={styles.propertyOption}>
        <span
          className={`${styles.cohortSwatch} ${swatch}`}
          aria-hidden="true"
        />
        {propertyLabel(item)}
      </span>
    );
  };

  // The blocked add's explanation. Held here (not in the working config) because a
  // REJECTED edit must leave the config untouched — the group keeps the wells it
  // had, and Carbon re-renders from `selectedItems` so the offending row drops out
  // of the pill list on its own.
  const [rateError, setRateError] = useState<string | null>(null);

  /**
   * Apply a well-selection change, or REJECT it when it would put two different
   * interests in one group (INS-10673). A group prices a single interest, so the
   * whole change is refused rather than partially applied — a partial apply would
   * silently drop the property the user just picked.
   *
   * @param {Array<PropertyOption | undefined> | null} [selectedItems] - Carbon's next selection (it types the array as possibly sparse).
   * @return {void}
   */
  const handleWellsChange = (
    selectedItems?: Array<PropertyOption | undefined> | null
  ): void => {
    // Carbon never includes the select-all sentinel in the selection, but guard
    // anyway so it can never leak into a group's well ids.
    const next = (selectedItems ?? []).filter(
      (well): well is PropertyOption => !!well && !well.isSelectAll
    );
    if (splitWellsByInterest(next).length > 1) {
      if (selectedWells.length === 0) {
        // The group holds nothing, so it prices nothing yet — the clash is WITHIN
        // the batch. Saying "this group prices X" here would quote the first well
        // of the rejected batch as though the group had committed to it.
        const rates = new Set(next.map(wellRateKey));
        setRateError(
          `These properties carry ${rates.size} different interests. ` +
            `${group.name} prices one interest — add the properties that share a ` +
            'rate, and put the rest in their own groups.'
        );
        return;
      }
      // The rate the group is established on: the first property it holds.
      const established = selectedWells[0];
      const establishedKey = wellRateKey(established);
      const offender =
        next.find((well) => wellRateKey(well) !== establishedKey) ?? next[0];
      setRateError(
        `${offender.name} carries ${rateLabel(offender)}, but ${
          group.name
        } prices ${rateLabel(established)}. ` +
          'A group prices one interest — put it in its own group.'
      );
      return;
    }
    setRateError(null);
    onChange(next);
  };

  return (
    <div className={styles.picker}>
      <span className={styles.sectionLabel}>
        Properties (<FormattedCount value={group.wellIds.length} />/
        <FormattedCount value={wells.length} />)
      </span>
      <FilterableMultiSelect
        id={`${group.id}-wells`}
        size="sm"
        titleText=""
        placeholder="Add properties"
        items={propertyItems}
        itemToString={propertyLabel}
        itemToElement={PropertyItem}
        sortItems={sortByCohort}
        selectedItems={selectedWells}
        onChange={({ selectedItems }) => handleWellsChange(selectedItems)}
      />
      {rateError && (
        <InlineNotification
          kind="error"
          lowContrast
          role="alert"
          title="Different interest"
          subtitle={rateError}
          onCloseButtonClick={() => setRateError(null)}
        />
      )}
    </div>
  );
}
